import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, Users, Settings, LogOut, BarChart3, Shield, Database, Globe, TrendingUp, ShoppingBag, Heart, Activity, KeyRound, Scale, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import PadaidLogo from '../../components/ui/PadaidLogo';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../services/api';
import axios from 'axios';

const API = `${API_BASE_URL}/api`;

const formatEUR = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));

const FounderDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ revenue_total: 0, total_sales_amount: 0, total_donation_amount: 0, total_members: 0, active_projects: 0, countries_served: 0 });
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ members_count: '', active_projects: '', impact: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    setPwErr(''); setPwMsg('');
    if (pwForm.new_password.length < 8) { setPwErr('Le nouveau mot de passe doit contenir au moins 8 caractères'); return; }
    if (pwForm.new_password !== pwForm.confirm) { setPwErr('Les mots de passe ne correspondent pas'); return; }
    try {
      setPwSaving(true);
      await axios.post(`${API}/user/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      setPwMsg('✅ Mot de passe mis à jour avec succès');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      setPwErr(e.response?.data?.detail || e.message);
    } finally { setPwSaving(false); }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [statsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/cleanup/summary`, { withCredentials: true })
      ]);

      setStats(statsRes.data || {});
      setSummary(summaryRes.data || {});

      // Pré-remplir le formulaire avec les métriques existantes si disponibles
      if (summaryRes.data?.metrics) {
        setForm({
          members_count: summaryRes.data.metrics.members_count ?? '',
          active_projects: summaryRes.data.metrics.active_projects ?? '',
          impact: summaryRes.data.metrics.impact ?? ''
        });
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveMetrics = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        set_members_count: form.members_count !== '' ? Number(form.members_count) : undefined,
        set_active_projects: form.active_projects !== '' ? Number(form.active_projects) : undefined,
        set_impact: form.impact !== '' ? Number(form.impact) : undefined,
      };

      const res = await axios.post(`${API}/admin/cleanup/metrics`, payload, { withCredentials: true });
      setSuccess('✅ Métriques mises à jour');
      await fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-destructive/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <PadaidLogo size="md" showText={true} />
              <div className="ml-4">
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground">Espace</span>
                  <Badge variant="destructive" className="ml-2">
                    <Crown className="w-3 h-3 mr-1" />
                    Fondateur
                  </Badge>
                </div>
                <h1 className="text-lg font-semibold text-destructive">Contrôle Total</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="default" size="sm" asChild data-testid="btn-admin-center-header">
                <Link to="/dashboard/fondateur/admin">
                  <Settings className="h-4 w-4 mr-2" />
                  Centre d'administration
                </Link>
              </Button>
              <Button variant="ghost" size="sm" data-testid="founder-logout-btn" onClick={async () => {
                await logout();
                navigate('/auth');
              }}>
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-4 text-sm text-red-500">{error}</div>
        )}
        {success && (
          <div className="mb-4 text-sm text-green-500">{success}</div>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Bienvenue, Abakar ALI
          </h2>
          <p className="text-muted-foreground">
            Fondateur de PADAID - Contrôle complet de l'organisation avec tous les pouvoirs administratifs
          </p>
        </div>

        {/* Accès rapide: Centre d'administration unifié */}
        <Link to="/dashboard/fondateur/admin" data-testid="admin-center-banner">
          <Card className="mb-8 border-primary/30 bg-gradient-to-r from-primary/10 to-destructive/5 hover:shadow-lg transition-all hover:-translate-y-0.5">
            <CardContent className="p-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="rounded-xl bg-primary/15 text-primary p-3">
                  <Settings className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Centre d'administration</h3>
                  <p className="text-sm text-muted-foreground">Tout gérer depuis un seul endroit : contenu, comptes, sécurité, réglages et code source.</p>
                </div>
              </div>
              <Button className="shrink-0" data-testid="btn-open-admin-center">Ouvrir</Button>
            </CardContent>
          </Card>
        </Link>

        {/* Executive Summary – valeurs réelles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          <Card className="border-destructive/20 bg-destructive/5">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenus Totaux Confirmés</CardTitle>
              <TrendingUp className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {loading ? '—' : formatEUR(stats.revenue_total)}
              </div>
              <p className="text-xs text-muted-foreground">Somme des transactions payées</p>
              <Progress value={stats.revenue_total ? 80 : 10} className="mt-2" />
            </CardContent>
          </Card>

          <Card className="border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Membres Réels</CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '—' : (stats.total_members ?? 0)}</div>
              <p className="text-xs text-muted-foreground">Excluant comptes de test</p>
              <Progress value={stats.total_members ? 60 : 10} className="mt-2" />
            </CardContent>
          </Card>

          <Card className="border-secondary/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Projets Actifs</CardTitle>
              <BarChart3 className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '—' : (stats.active_projects ?? 0)}</div>
              <p className="text-xs text-muted-foreground">Valeur éditable</p>
              <Progress value={stats.active_projects ? 70 : 5} className="mt-2" />
            </CardContent>
          </Card>

          <Card className="border-accent/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Impact (Pays/Communautés)</CardTitle>
              <Globe className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '—' : (stats.countries_served ?? 0)}</div>
              <p className="text-xs text-muted-foreground">Valeur éditable</p>
              <Progress value={stats.countries_served ? 90 : 5} className="mt-2" />
            </CardContent>
          </Card>

          {/* New tile: Ventes */}
          <Card className="border-emerald-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Ventes</CardTitle>
              <ShoppingBag className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '—' : formatEUR(stats.total_sales_amount)}</div>
              <p className="text-xs text-muted-foreground">Produits boutique confirmés</p>
              <Progress value={stats.total_sales_amount ? 50 : 5} className="mt-2" />
            </CardContent>
          </Card>

          {/* New tile: Dons */}
          <Card className="border-pink-500/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Dons</CardTitle>
              <Heart className="h-4 w-4 text-pink-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '—' : formatEUR(stats.total_donation_amount)}</div>
              <p className="text-xs text-muted-foreground">Total dons confirmés</p>
              <Progress value={stats.total_donation_amount ? 65 : 5} className="mt-2" />
            </CardContent>
          </Card>

        </div>

        {/* Bloc métriques éditables */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Métriques Réelles (édition)</CardTitle>
            <CardDescription>Définissez ici vos chiffres réels. Ils s'affichent sur le site et remplacent toute valeur factice.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1">Membres (optionnel)</label>
                <input
                  type="number"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                  value={form.members_count}
                  onChange={e => setForm({ ...form, members_count: e.target.value })}
                  placeholder="ex: 3"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Projets actifs</label>
                <input
                  type="number"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                  value={form.active_projects}
                  onChange={e => setForm({ ...form, active_projects: e.target.value })}
                  placeholder="ex: 2"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Impact (pays/communautés)</label>
                <input
                  type="number"
                  className="w-full rounded-md border border-border bg-background px-3 py-2"
                  value={form.impact}
                  onChange={e => setForm({ ...form, impact: e.target.value })}
                  placeholder="ex: 5"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveMetrics} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Sauvegarder les métriques'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Founder Controls (inchangé) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="hover:shadow-lg transition-shadow border-destructive/20">
            <CardHeader className="text-center">
              <Shield className="h-8 w-8 text-destructive mx-auto mb-2" />
              <CardTitle className="text-lg">Authentification 2FA</CardTitle>
              <CardDescription>Configuration sécurité double facteur</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-destructive hover:bg-destructive/90" asChild>
                <Link to="/dashboard/fondateur/2fa">Configurer 2FA</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-orange-500/20">
            <CardHeader className="text-center">
              <Database className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Administration Système</CardTitle>
              <CardDescription>Contrôle technique complet du système</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-orange-500 hover:bg-orange-600" asChild>
                <Link to="/dashboard/fondateur/system">Admin Système</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-primary/20">
            <CardHeader className="text-center">
              <Users className="h-8 w-8 text-primary mx-auto mb-2" />
              <CardTitle className="text-lg">Gestion Utilisateurs</CardTitle>
              <CardDescription>Contrôle total des comptes utilisateurs</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-primary hover:bg-primary/90" asChild>
                <Link to="/dashboard/fondateur/console">Gérer Utilisateurs</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-emerald-500/20" data-testid="conformite-card">
            <CardHeader className="text-center">
              <Scale className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <CardTitle className="text-lg">Conformité juridique</CardTitle>
              <CardDescription>Revue mensuelle automatique selon le droit français</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" asChild data-testid="btn-conformite">
                <Link to="/dashboard/fondateur/conformite">Voir la conformité</Link>
              </Button>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-shadow border-red-500/30" data-testid="admin-console-card">
            <CardHeader className="text-center">
              <ShieldAlert className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Centre de sécurité & comptes</CardTitle>
              <CardDescription>Bloquer des IP, purger, gérer/supprimer des comptes, déconnexion à distance</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-red-500 hover:bg-red-600" asChild data-testid="btn-admin-console">
                <Link to="/dashboard/fondateur/console">Ouvrir le centre</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Flux de données / Audit + Sécurité */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-blue-500/20" data-testid="audit-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Activity className="h-6 w-6 text-blue-500" />
                <CardTitle className="text-lg">Flux de données & Audit</CardTitle>
              </div>
              <CardDescription>
                Visualisez qui a créé un compte, qui s'est connecté, avec la date, l'heure et l'adresse IP.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full bg-blue-500 hover:bg-blue-600" asChild data-testid="btn-audit-logins">
                <Link to="/admin/audit-logs">Connexions & Inscriptions</Link>
              </Button>
              <Button variant="outline" className="w-full" asChild data-testid="btn-audit-events">
                <Link to="/admin/audit/users">Activité du site (pages vues, évènements)</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-amber-500/20" data-testid="change-password-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <KeyRound className="h-6 w-6 text-amber-500" />
                <CardTitle className="text-lg">Changer le mot de passe</CardTitle>
              </div>
              <CardDescription>Modifiez votre mot de passe temporaire ici.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {pwErr && <div className="text-sm text-red-500" data-testid="pw-error">{pwErr}</div>}
              {pwMsg && <div className="text-sm text-green-500" data-testid="pw-success">{pwMsg}</div>}
              <input
                type="password"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
                placeholder="Mot de passe actuel"
                value={pwForm.current_password}
                onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                data-testid="pw-current"
              />
              <input
                type="password"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
                placeholder="Nouveau mot de passe (min. 8 caractères)"
                value={pwForm.new_password}
                onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                data-testid="pw-new"
              />
              <input
                type="password"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
                placeholder="Confirmer le nouveau mot de passe"
                value={pwForm.confirm}
                onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
                data-testid="pw-confirm"
              />
              <Button onClick={changePassword} disabled={pwSaving} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="btn-change-password">
                {pwSaving ? 'Enregistrement…' : 'Mettre à jour le mot de passe'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sections restantes conservées (état système, etc.) */}
      </div>
    </div>
  );
};

export default FounderDashboard;